import React, { useState, useEffect } from 'react';
import { Heart, MapPin, Trash2, Calendar, ArrowRight, Compass } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext'; // 👈 Theme Support Added

const WishlistPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme(); // 👈 Access Theme State
  const [wishlist, setWishlist] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const formatImageUrl = (backendPath) => {
    if (!backendPath) return "https://images.unsplash.com/photo-1506461883276-594a12b11cf3?auto=format&fit=crop&w=800&q=80"; 
    if (backendPath.startsWith('http://localhost') || backendPath.startsWith('http://127.0.0.1')) {
      const url = new URL(backendPath);
      return url.pathname;
    }
    if (backendPath.startsWith('http')) return backendPath;
    const cleanPath = backendPath.replace('backend/', '');
    return cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  };

  useEffect(() => {
    const fetchWishlist = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setIsLoading(false);
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`/api/get_wishlist`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setWishlist(data.wishlist || []);
        } else if (response.status === 401) {
          alert("Your session has expired. Please log in again.");
          localStorage.removeItem('access_token');
          navigate('/login');
        }
      } catch (error) {
        console.error("Failed to load wishlist:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWishlist();
  }, [navigate]);

  const handleRemove = async (e, nameToRemove) => {
    e.stopPropagation();
    
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`/api/remove_wishlist/${encodeURIComponent(nameToRemove)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setWishlist(prev => prev.filter(item => item.name !== nameToRemove));
      } else if (response.status === 401) {
        alert("Your session has expired. Please log in again.");
        localStorage.removeItem('access_token');
        navigate('/login');
      } else {
        const err = await response.json();
        alert(`Failed to remove: ${err.detail}`);
      }
    } catch (error) {
      console.error("Error removing from wishlist:", error);
      alert("Server error occurred while removing the item.");
    }
  };

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: isDark ? '#111827' : '#f9fafb', transition: 'background-color 0.3s' }}>
        <p style={{ fontSize: '18px', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 'bold' }}>Loading your wishlist...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: isDark ? '#111827' : '#f9fafb', fontFamily: 'system-ui, -apple-system, sans-serif', paddingBottom: '80px', paddingTop: '90px', transition: 'background-color 0.3s', boxSizing: 'border-box' }}>
      
      {/* HEADER SECTION */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px', marginBottom: '35px', textAlign: 'center', boxSizing: 'border-box' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '56px', height: '56px', backgroundColor: isDark ? '#500724' : '#fee2e2', borderRadius: '50%', marginBottom: '16px' }}>
          <Heart size={28} color="#dc2626" fill="#dc2626" />
        </div>
        <h1 style={{ fontSize: 'clamp(32px, 5vw, 42px)', fontWeight: '800', color: isDark ? '#f9fafb' : '#111827', marginBottom: '12px', letterSpacing: '-1px' }}>
          Your Travel Wishlist
        </h1>
        <p style={{ fontSize: '15px', color: isDark ? '#9ca3af' : '#6b7280', maxWidth: '600px', margin: '0 auto', lineHeight: '1.5' }}>
          All the beautiful destinations you've saved. Ready to turn these dreams into itineraries?
        </p>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px', boxSizing: 'border-box' }}>
        {wishlist.length === 0 ? (
          /* EMPTY STATE */
          <div style={{ backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', padding: '50px 20px', textAlign: 'center', border: isDark ? '1px dashed #4b5563' : '1px dashed #d1d5db', boxShadow: isDark ? '0 10px 25px rgba(0,0,0,0.5)' : '0 10px 25px rgba(0,0,0,0.02)', transition: 'background-color 0.3s, border-color 0.3s', boxSizing: 'border-box' }}>
            <Compass size={50} color="#9ca3af" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#1f2937', marginBottom: '8px' }}>Your wishlist is empty</h3>
            <p style={{ color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '25px', fontSize: '14px', lineHeight: '1.5' }}>You haven't saved any destinations yet. Start exploring and click the heart icon to save places here.</p>
            <button 
              onClick={() => navigate('/discover')}
              style={{ backgroundColor: '#16a34a', color: 'white', padding: '12px 24px', borderRadius: '50px', fontSize: '15px', fontWeight: 'bold', border: 'none', cursor: 'pointer', transition: 'background 0.2s' }}
              onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='#15803d'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='#16a34a'}
            >
              Discover Destinations
            </button>
          </div>
        ) : (
          /* WISHLIST GRID */
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '25px' }}>
            {wishlist.map((item, idx) => (
              <div 
                key={item.id || idx}
                onClick={() => navigate(`/place/${item.name}`)}
                style={{ 
                  backgroundColor: isDark ? '#1f2937' : 'white', borderRadius: '20px', overflow: 'hidden', border: isDark ? '1px solid #374151' : '1px solid #e5e7eb', 
                  boxShadow: isDark ? '0 10px 25px rgba(0,0,0,0.5)' : '0 10px 25px rgba(0,0,0,0.04)', transition: 'transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s, border-color 0.3s', 
                  cursor: 'pointer', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' 
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = isDark ? '0 20px 40px rgba(0,0,0,0.7)' : '0 20px 40px rgba(0,0,0,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = isDark ? '0 10px 25px rgba(0,0,0,0.5)' : '0 10px 25px rgba(0,0,0,0.04)'; }}
              >
                <div style={{ height: '200px', position: 'relative', backgroundColor: isDark ? '#374151' : '#e5e7eb' }}>
                  <img src={formatImageUrl(item.image)} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button 
                    onClick={(e) => handleRemove(e, item.name)}
                    style={{ position: 'absolute', top: '12px', right: '12px', width: '34px', height: '34px', borderRadius: '50%', backgroundColor: isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', color: '#ef4444', transition: 'all 0.2s' }}
                    onMouseEnter={(e)=>e.currentTarget.style.backgroundColor=isDark ? '#500724' : '#fee2e2'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor=isDark ? 'rgba(31, 41, 55, 0.9)' : 'rgba(255,255,255,0.9)'}
                    title="Remove from wishlist"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', flex: 1, boxSizing: 'border-box' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: isDark ? '#f9fafb' : '#111827', marginBottom: '6px' }}>
                    {item.name}
                  </h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: isDark ? '#4ade80' : '#16a34a', marginBottom: '12px', fontWeight: '600' }}>
                    <MapPin size={13} /> India
                  </div>
                  <p style={{ fontSize: '13px', color: isDark ? '#d1d5db' : '#4b5563', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1, marginBottom: '20px' }}>
                    {item.excerpt || "A beautiful destination waiting to be explored."}
                  </p>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 'auto' }}>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/plan', { state: { presetDestination: item.name } });
                      }}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', backgroundColor: '#16a34a', color: 'white', padding: '10px 14px', borderRadius: '10px', fontSize: '13px', fontWeight: 'bold', border: 'none', cursor: 'pointer', transition: 'background 0.2s' }}
                      onMouseEnter={(e)=>e.currentTarget.style.backgroundColor='#15803d'} onMouseLeave={(e)=>e.currentTarget.style.backgroundColor='#16a34a'}
                    >
                      <Calendar size={15} /> Plan Trip
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px', backgroundColor: isDark ? '#374151' : '#f3f4f6', borderRadius: '10px', color: isDark ? '#d1d5db' : '#4b5563', flexShrink: 0 }}>
                      <ArrowRight size={16} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;